import React, { useState, useEffect, useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from "chart.js";
import { Line, Bar, Doughnut, Pie } from "react-chartjs-2";
import { 
  FaDollarSign, 
  FaShoppingBag, 
  FaUsers, 
  FaChartLine, 
  FaCalendarAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaShippingFast,
  FaClock,
  FaExclamationTriangle,
  FaRedo,
  FaStar
} from "react-icons/fa";
import "./AnalyticsDashboard.css";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const AnalyticsDashboard = ({ orders, users, submissions, onNavigateToTab }) => {
  const [timeRange, setTimeRange] = useState("30"); // days
  const [loading, setLoading] = useState(false);

  // Filter orders by time range
  const filteredOrders = useMemo(() => {
    if (!orders || orders.length === 0) return [];
    
    const days = parseInt(timeRange);
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    return orders.filter(order => {
      const orderDate = new Date(order.order_date || order.orderDate || order.created_at);
      return orderDate >= cutoffDate;
    });
  }, [orders, timeRange]);

  // Calculate Business Performance Metrics
  const businessMetrics = useMemo(() => {
    if (!filteredOrders || filteredOrders.length === 0) {
      return {
        // Basic Stats
        totalRevenue: 0,
        totalOrders: 0,
        totalUsers: users?.length || 0,
        avgOrderValue: 0,
        completedOrders: 0,
        pendingOrders: 0,
        // Sales/Finance KPIs
        conversionRate: 0,
        paymentSuccessRate: 0,
        returnRefundRate: 0,
        // Fulfillment KPIs
        avgOrderProcessingTime: 0,
        avgTimeToShip: 0,
        onTimeDeliveryRate: 0,
        delayedOrdersPercent: 0,
        // Customer Experience KPIs
        customerComplaints: 0,
        repeatCustomerRate: 0,
        npsScore: 0
      };
    }

    const totalRevenue = filteredOrders.reduce((sum, order) => {
      const total = parseFloat(order.total) || 0;
      return sum + total;
    }, 0);

    const completedOrders = filteredOrders.filter(
      order => order.order_status === "delivered" || order.payment_status === "payment_completed"
    ).length;

    const pendingOrders = filteredOrders.filter(
      order => order.payment_status === "payment_pending" || !order.payment_status
    ).length;

    // Sales/Finance KPIs
    // Conversion Rate: Orders / Users (proxy since we don't have visitor data)
    const conversionRate = users?.length > 0 ? (filteredOrders.length / users.length) * 100 : 0;
    
    // Payment Success Rate
    const successfulPayments = filteredOrders.filter(
      order => order.payment_status === "payment_completed"
    ).length;
    const paymentSuccessRate = filteredOrders.length > 0 
      ? (successfulPayments / filteredOrders.length) * 100 
      : 0;

    // Return/Refund Rate
    const returnedOrders = filteredOrders.filter(
      order => order.return_status || order.refund_status
    ).length;
    const returnRefundRate = filteredOrders.length > 0 
      ? (returnedOrders / filteredOrders.length) * 100 
      : 0;

    // Fulfillment KPIs
    // Order Processing Time (time from order creation to status update)
    const processingTimes = filteredOrders
      .filter(order => order.status_updated_at && order.order_date)
      .map(order => {
        const orderDate = new Date(order.order_date || order.orderDate || order.created_at);
        const statusUpdateDate = new Date(order.status_updated_at);
        return (statusUpdateDate - orderDate) / (1000 * 60 * 60); // hours
      })
      .filter(time => time > 0 && time < 720); // Filter out invalid times (0-30 days)
    
    const avgOrderProcessingTime = processingTimes.length > 0
      ? processingTimes.reduce((sum, time) => sum + time, 0) / processingTimes.length
      : 0;

    // Time to Ship (TTS)
    const timeToShipData = filteredOrders
      .filter(order => order.shipment_status && (order.order_date || order.created_at))
      .map(order => {
        const orderDate = new Date(order.order_date || order.orderDate || order.created_at);
        // Use status_updated_at as proxy for shipment date if delivery_timestamp not available
        const shipDate = order.delivery_timestamp 
          ? new Date(order.delivery_timestamp)
          : order.status_updated_at 
            ? new Date(order.status_updated_at)
            : null;
        if (!shipDate) return null;
        return (shipDate - orderDate) / (1000 * 60 * 60 * 24); // days
      })
      .filter(time => time !== null && time > 0 && time < 90); // Filter valid times (0-90 days)
    
    const avgTimeToShip = timeToShipData.length > 0
      ? timeToShipData.reduce((sum, time) => sum + time, 0) / timeToShipData.length
      : 0;

    // On-time Delivery Rate
    const deliveredOrders = filteredOrders.filter(
      order => order.delivery_timestamp && order.estimated_delivery_date
    );
    const onTimeDeliveries = deliveredOrders.filter(order => {
      const deliveryDate = new Date(order.delivery_timestamp);
      const estimatedDate = new Date(order.estimated_delivery_date);
      return deliveryDate <= estimatedDate;
    }).length;
    const onTimeDeliveryRate = deliveredOrders.length > 0
      ? (onTimeDeliveries / deliveredOrders.length) * 100
      : 0;

    // Delayed Orders Percentage
    const delayedOrders = deliveredOrders.length - onTimeDeliveries;
    const delayedOrdersPercent = deliveredOrders.length > 0
      ? (delayedOrders / deliveredOrders.length) * 100
      : 0;

    // Customer Experience KPIs
    // Customer Complaints
    const customerComplaints = filteredOrders.filter(
      order => order.complaint_ticket_id
    ).length;

    // Repeat Customer Rate
    const userOrderCounts = {};
    filteredOrders.forEach(order => {
      const userId = order.userId || order.contact || order.email;
      if (userId) {
        userOrderCounts[userId] = (userOrderCounts[userId] || 0) + 1;
      }
    });
    const repeatCustomers = Object.values(userOrderCounts).filter(count => count > 1).length;
    const uniqueCustomers = Object.keys(userOrderCounts).length;
    const repeatCustomerRate = uniqueCustomers > 0
      ? (repeatCustomers / uniqueCustomers) * 100
      : 0;

    // NPS Score (proxy from customer_satisfaction)
    const satisfactionScores = filteredOrders
      .filter(order => order.customer_satisfaction)
      .map(order => {
        // Try to extract numeric score from satisfaction text
        const satisfaction = order.customer_satisfaction.toLowerCase();
        if (satisfaction.includes("excellent") || satisfaction.includes("5") || satisfaction.includes("very satisfied")) return 9;
        if (satisfaction.includes("good") || satisfaction.includes("4") || satisfaction.includes("satisfied")) return 7;
        if (satisfaction.includes("average") || satisfaction.includes("3") || satisfaction.includes("neutral")) return 5;
        if (satisfaction.includes("poor") || satisfaction.includes("2") || satisfaction.includes("dissatisfied")) return 3;
        if (satisfaction.includes("very poor") || satisfaction.includes("1") || satisfaction.includes("very dissatisfied")) return 1;
        // Try to parse as number
        const numMatch = satisfaction.match(/\d+/);
        if (numMatch) {
          const num = parseInt(numMatch[0]);
          if (num >= 1 && num <= 10) return num;
        }
        return null;
      })
      .filter(score => score !== null);
    
    const promoters = satisfactionScores.filter(score => score >= 9).length;
    const detractors = satisfactionScores.filter(score => score <= 6).length;
    const npsScore = satisfactionScores.length > 0
      ? ((promoters - detractors) / satisfactionScores.length) * 100
      : 0;

    return {
      // Basic Stats
      totalRevenue,
      totalOrders: filteredOrders.length,
      totalUsers: users?.length || 0,
      avgOrderValue: filteredOrders.length > 0 ? totalRevenue / filteredOrders.length : 0,
      completedOrders,
      pendingOrders,
      // Sales/Finance KPIs
      conversionRate,
      paymentSuccessRate,
      returnRefundRate,
      // Fulfillment KPIs
      avgOrderProcessingTime,
      avgTimeToShip,
      onTimeDeliveryRate,
      delayedOrdersPercent,
      // Customer Experience KPIs
      customerComplaints,
      repeatCustomerRate,
      npsScore
    };
  }, [filteredOrders, users]);

  // Revenue over time (Line Chart)
  const revenueData = useMemo(() => {
    const days = parseInt(timeRange);
    const data = Array(days).fill(0);
    const labels = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      labels.push(date.toLocaleDateString("en-US", { month: "short", day: "numeric" }));

      filteredOrders.forEach(order => {
        const orderDate = new Date(order.order_date || order.orderDate || order.created_at);
        if (
          orderDate.toDateString() === date.toDateString()
        ) {
          data[days - 1 - i] += parseFloat(order.total) || 0;
        }
      });
    }

    return {
      labels,
      datasets: [
        {
          label: "Revenue",
          data: data,
          borderColor: "rgb(102, 126, 234)",
          backgroundColor: "rgba(102, 126, 234, 0.1)",
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6
        }
      ]
    };
  }, [filteredOrders, timeRange]);

  // Order status distribution (Doughnut Chart)
  const orderStatusData = useMemo(() => {
    const statusCounts = {};
    filteredOrders.forEach(order => {
      const status = order.order_status || "pending";
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    const labels = Object.keys(statusCounts);
    const data = Object.values(statusCounts);
    const colors = [
      "rgba(102, 126, 234, 0.8)",
      "rgba(118, 75, 162, 0.8)",
      "rgba(255, 99, 132, 0.8)",
      "rgba(54, 162, 235, 0.8)",
      "rgba(255, 206, 86, 0.8)",
      "rgba(75, 192, 192, 0.8)",
      "rgba(153, 102, 255, 0.8)"
    ];

    return {
      labels: labels.map(label => label.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())),
      datasets: [
        {
          data: data,
          backgroundColor: colors.slice(0, labels.length),
          borderWidth: 2,
          borderColor: "#fff"
        }
      ]
    };
  }, [filteredOrders]);

  // Payment status distribution (Pie Chart)
  const paymentStatusData = useMemo(() => {
    const statusCounts = {};
    filteredOrders.forEach(order => {
      const status = order.payment_status || "payment_pending";
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    const labels = Object.keys(statusCounts);
    const data = Object.values(statusCounts);
    const colors = [
      "rgba(75, 192, 192, 0.8)", // completed - green
      "rgba(255, 206, 86, 0.8)", // pending - yellow
      "rgba(255, 99, 132, 0.8)", // failed - red
      "rgba(153, 102, 255, 0.8)", // refunded - purple
      "rgba(54, 162, 235, 0.8)" // other - blue
    ];

    return {
      labels: labels.map(label => label.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())),
      datasets: [
        {
          data: data,
          backgroundColor: colors.slice(0, labels.length),
          borderWidth: 2,
          borderColor: "#fff"
        }
      ]
    };
  }, [filteredOrders]);

  // Orders per day (Bar Chart)
  const ordersPerDayData = useMemo(() => {
    const days = parseInt(timeRange);
    const data = Array(days).fill(0);
    const labels = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      labels.push(date.toLocaleDateString("en-US", { month: "short", day: "numeric" }));

      filteredOrders.forEach(order => {
        const orderDate = new Date(order.order_date || order.orderDate || order.created_at);
        if (orderDate.toDateString() === date.toDateString()) {
          data[days - 1 - i]++;
        }
      });
    }

    return {
      labels,
      datasets: [
        {
          label: "Orders",
          data: data,
          backgroundColor: "rgba(102, 126, 234, 0.6)",
          borderColor: "rgba(102, 126, 234, 1)",
          borderWidth: 1
        }
      ]
    };
  }, [filteredOrders, timeRange]);

  // Top performing days (Bar Chart)
  const topDaysData = useMemo(() => {
    const dayRevenue = {};
    
    filteredOrders.forEach(order => {
      const orderDate = new Date(order.order_date || order.orderDate || order.created_at);
      const dateKey = orderDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      dayRevenue[dateKey] = (dayRevenue[dateKey] || 0) + (parseFloat(order.total) || 0);
    });

    const sorted = Object.entries(dayRevenue)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 7);

    return {
      labels: sorted.map(([date]) => date),
      datasets: [
        {
          label: "Revenue",
          data: sorted.map(([, revenue]) => revenue),
          backgroundColor: "rgba(118, 75, 162, 0.6)",
          borderColor: "rgba(118, 75, 162, 1)",
          borderWidth: 1
        }
      ]
    };
  }, [filteredOrders]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        mode: "index",
        intersect: false,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0, 0, 0, 0.05)"
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
      }
    }
  };

  return (
    <div className="analytics-dashboard">
      <div className="analytics-header">
        <div>
          <h2 className="analytics-title">📊 Analytics Dashboard</h2>
          <p className="analytics-subtitle">Track your business performance and insights</p>
        </div>
        <div className="time-range-selector">
          <FaCalendarAlt />
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="time-range-select"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
        </div>
      </div>

      {/* Basic Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card revenue">
          <div className="stat-icon">
            <FaDollarSign />
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Revenue</div>
            <div className="stat-value">
              ${businessMetrics.totalRevenue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>

        <div 
          className="stat-card orders clickable" 
          onClick={() => onNavigateToTab && onNavigateToTab("orders")}
          style={{ cursor: onNavigateToTab ? "pointer" : "default" }}
          title={onNavigateToTab ? "Click to view orders" : ""}
        >
          <div className="stat-icon">
            <FaShoppingBag />
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Orders</div>
            <div className="stat-value">{businessMetrics.totalOrders}</div>
          </div>
        </div>

        <div 
          className="stat-card users clickable" 
          onClick={() => onNavigateToTab && onNavigateToTab("users")}
          style={{ cursor: onNavigateToTab ? "pointer" : "default" }}
          title={onNavigateToTab ? "Click to view users" : ""}
        >
          <div className="stat-icon">
            <FaUsers />
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Users</div>
            <div className="stat-value">{businessMetrics.totalUsers}</div>
          </div>
        </div>

        <div className="stat-card avg">
          <div className="stat-icon">
            <FaChartLine />
          </div>
          <div className="stat-content">
            <div className="stat-label">Avg Order Value (AOV)</div>
            <div className="stat-value">
              ${businessMetrics.avgOrderValue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>
      </div>
{/* Charts Grid */}
<div className="charts-grid">
        {/* Revenue Over Time */}
        <div className="chart-card">
          <h3 className="chart-title">Revenue Over Time</h3>
          <div className="chart-container">
            <Line data={revenueData} options={chartOptions} />
          </div>
        </div>

        {/* Orders Per Day */}
        <div className="chart-card">
          <h3 className="chart-title">Orders Per Day</h3>
          <div className="chart-container">
            <Bar data={ordersPerDayData} options={chartOptions} />
          </div>
        </div>

        {/* Order Status Distribution */}
        <div className="chart-card">
          <h3 className="chart-title">Order Status Distribution</h3>
          <div className="chart-container">
            <Doughnut data={orderStatusData} options={pieOptions} />
          </div>
        </div>

        {/* Payment Status Distribution */}
        <div className="chart-card">
          <h3 className="chart-title">Payment Status</h3>
          <div className="chart-container">
            <Pie data={paymentStatusData} options={pieOptions} />
          </div>
        </div>

        {/* Top Performing Days */}
        <div className="chart-card full-width">
          <h3 className="chart-title">Top Performing Days (Revenue)</h3>
          <div className="chart-container">
            <Bar data={topDaysData} options={chartOptions} />
          </div>
        </div>
      </div>
      {/* Sales/Finance KPIs */}
      <div className="kpi-section">
        <h3 className="kpi-section-title">💰 Sales & Finance KPIs</h3>
        <div className="kpi-grid">
          <div className="kpi-card">
            <div className="kpi-header">
              <FaChartLine className="kpi-icon" />
              <span className="kpi-label">Conversion Rate</span>
            </div>
            <div className="kpi-value">{businessMetrics.conversionRate.toFixed(1)}%</div>
            <div className="kpi-description">Orders per user</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-header">
              <FaCheckCircle className="kpi-icon success" />
              <span className="kpi-label">Payment Success Rate</span>
            </div>
            <div className="kpi-value">{businessMetrics.paymentSuccessRate.toFixed(1)}%</div>
            <div className="kpi-description">Successful payments</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-header">
              <FaRedo className="kpi-icon warning" />
              <span className="kpi-label">Return/Refund Rate</span>
            </div>
            <div className="kpi-value">{businessMetrics.returnRefundRate.toFixed(1)}%</div>
            <div className="kpi-description">Orders returned/refunded</div>
          </div>
        </div>
      </div>

      {/* Fulfillment KPIs */}
      <div className="kpi-section">
        <h3 className="kpi-section-title">📦 Fulfillment KPIs</h3>
        <div className="kpi-grid">
          <div className="kpi-card">
            <div className="kpi-header">
              <FaClock className="kpi-icon" />
              <span className="kpi-label">Avg Processing Time</span>
            </div>
            <div className="kpi-value">
              {businessMetrics.avgOrderProcessingTime > 0 
                ? `${businessMetrics.avgOrderProcessingTime.toFixed(1)} hrs`
                : "N/A"}
            </div>
            <div className="kpi-description">Order to status update</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-header">
              <FaShippingFast className="kpi-icon" />
              <span className="kpi-label">Time to Ship (TTS)</span>
            </div>
            <div className="kpi-value">
              {businessMetrics.avgTimeToShip > 0 
                ? `${businessMetrics.avgTimeToShip.toFixed(1)} days`
                : "N/A"}
            </div>
            <div className="kpi-description">Order to shipment</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-header">
              <FaCheckCircle className="kpi-icon success" />
              <span className="kpi-label">On-Time Delivery Rate</span>
            </div>
            <div className="kpi-value">{businessMetrics.onTimeDeliveryRate.toFixed(1)}%</div>
            <div className="kpi-description">Delivered on time</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-header">
              <FaExclamationTriangle className="kpi-icon warning" />
              <span className="kpi-label">Delayed Orders</span>
            </div>
            <div className="kpi-value">{businessMetrics.delayedOrdersPercent.toFixed(1)}%</div>
            <div className="kpi-description">Orders delayed</div>
          </div>
        </div>
      </div>

      {/* Customer Experience KPIs */}
      <div className="kpi-section">
        <h3 className="kpi-section-title">⭐ Customer Experience KPIs</h3>
        <div className="kpi-grid">
          <div className="kpi-card">
            <div className="kpi-header">
              <FaExclamationTriangle className="kpi-icon warning" />
              <span className="kpi-label">Customer Complaints</span>
            </div>
            <div className="kpi-value">{businessMetrics.customerComplaints}</div>
            <div className="kpi-description">Total complaint tickets</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-header">
              <FaUsers className="kpi-icon" />
              <span className="kpi-label">Repeat Customer Rate</span>
            </div>
            <div className="kpi-value">{businessMetrics.repeatCustomerRate.toFixed(1)}%</div>
            <div className="kpi-description">Customers with multiple orders</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-header">
              <FaStar className="kpi-icon" />
              <span className="kpi-label">NPS Score</span>
            </div>
            <div className="kpi-value">
              {businessMetrics.npsScore > 0 
                ? `${businessMetrics.npsScore.toFixed(0)}`
                : "N/A"}
            </div>
            <div className="kpi-description">Net Promoter Score</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
